import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, ContentChildren } from '@angular/core';

// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
function mergeItemChanges(target, changes) {
    const itemChangesOverrides = Object.entries(changes).reduce((acc, [propertyKey, change]) => Object.assign(acc, { [propertyKey]: change.currentValue }), {});
    return Object.assign({}, target, itemChangesOverrides);
}

// Copyright (c) Microsoft Corporation. All rights reserved.

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Helper class for single changeable item
 */
class ChangeableItemHelper {
    constructor(key) {
        this.key = key;
        this.onItemChanged = new EventEmitter();
    }
    /**
     * Action to be called by user of class when change is detected
     * (Typically called in ngOnChanges)
     * @param changes TypedChanges that are to be emitted
     */
    emitChanges(changes) {
        this.onItemChanged.emit({ key: this.key, changes });
    }
}
/**
 * Parent class for wrapper directive for multiple ChangeableItemDirectives
 */
class ChangeableItemsHelper {
    /**
     * Initialize subscriptions to watch for changes to children ChangeableItemDirectives
     * (Typically called in ngAfterContentInit after @ContentChildren are initialized)
     * @param directiveItems List of children to watch for
     * @param self Reference to component using this helper. This component gets filtered out in case
     *  it appears in the list of children (i.e. when a component has children of its own type)
     * @param nonSelfHandler Callback to handle filtered list of children when updated
     */
    constructor(directiveItems, self, nonSelfHandler) {
        this.directiveItems = directiveItems;
        this.self = self;
        this.nonSelfHandler = nonSelfHandler;
        this.onChildItemChanged = new EventEmitter();
        this.onItemsChanged = new EventEmitter();
        this._subscriptionsMap = {};
        this._subscribeNewDirectives();
        this._changeSubscription = this.directiveItems.changes.subscribe(newValues => {
            this.onItemsChanged.emit(newValues);
            this._subscribeNewDirectives();
        });
    }
    /**
     * Action to be called by user of class when directive/component is destroyed
     * (Typically called in ngOnDestroy)
     */
    destroy() {
        Object.values(this._subscriptionsMap).forEach(value => value.unsubscribe());
        this._changeSubscription.unsubscribe();
    }
    _subscribeNewDirectives() {
        const nonSelfDirectives = this._handleNonSelfDirectives();
        nonSelfDirectives.forEach(directiveItem => {
            if (this._subscriptionsMap[directiveItem.key]) {
                this._subscriptionsMap[directiveItem.key].unsubscribe();
            }
            this._subscriptionsMap[directiveItem.key] = directiveItem.onItemChanged.subscribe(changes => {
                this._handleNonSelfDirectives();
                this.onChildItemChanged.emit(changes);
                if (this.self && this.self.onItemChanged) {
                    this.self.onItemChanged.emit(changes);
                }
            });
        });
    }
    _handleNonSelfDirectives() {
        const nonSelfDirectives = this.directiveItems.filter(directiveItem => directiveItem !== this.self);
        if (this.nonSelfHandler && nonSelfDirectives.length) {
            this.nonSelfHandler(nonSelfDirectives);
        }
        return nonSelfDirectives;
    }
}

// Copyright (c) Microsoft Corporation. All rights reserved.
;
/**
 * Parent class for wrapper directive for single item with OnChanges
 */
class ChangeableItemDirective {
    get onItemChanged() {
        return this.changeableItemHelper && this.changeableItemHelper.onItemChanged;
    }
    ngOnInit() {
        this.changeableItemHelper = new ChangeableItemHelper(this.key);
    }
    ngOnChanges(changes) {
        if (this.changeableItemHelper) {
            this.changeableItemHelper.emitChanges(changes);
        }
    }
}
/** @nocollapse */ ChangeableItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ChangeableItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ ChangeableItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: ChangeableItemDirective, inputs: { key: "key" }, outputs: { onItemChanged: "onItemChanged" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ChangeableItemDirective, decorators: [{
            type: Directive
        }], propDecorators: { key: [{
                type: Input
            }], onItemChanged: [{
                type: Output
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Parent class for wrapper directive for multiple ChangeableItemDirectives
 */
class ChangeableItemsDirective {
    get onChildItemChanged() {
        return this.changeableItemsHelper && this.changeableItemsHelper.onChildItemChanged;
    }
    get onItemsChanged() {
        return this.changeableItemsHelper && this.changeableItemsHelper.onItemsChanged;
    }
    ngAfterContentInit() {
        this.changeableItemsHelper = new ChangeableItemsHelper(this.directiveItems);
    }
    ngOnDestroy() {
        this.changeableItemsHelper.destroy();
    }
}
/** @nocollapse */ ChangeableItemsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ChangeableItemsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ ChangeableItemsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: ChangeableItemsDirective, outputs: { onChildItemChanged: "onChildItemChanged", onItemsChanged: "onItemsChanged" }, queries: [{ propertyName: "directiveItems", predicate: ChangeableItemDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ChangeableItemsDirective, decorators: [{
            type: Directive
        }], propDecorators: { directiveItems: [{
                type: ContentChildren,
                args: [ChangeableItemDirective]
            }], onChildItemChanged: [{
                type: Output
            }], onItemsChanged: [{
                type: Output
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { ChangeableItemDirective, ChangeableItemHelper, ChangeableItemsDirective, ChangeableItemsHelper, mergeItemChanges };
